import { useEffect, useLayoutEffect, useState } from "react";
import useOnlineTracker from "./hooks/useOnlineTracker";
import "./cmsCritical.css";

// CSS and JS files required by the AdminLTE-based CMS layout.
// They live under /assets/cms/assets/ in the public folder; CMS overrides in /assets/cms_custom.css.
const CMS_CSS = [
  "/assets/cms/assets/bower_components/bootstrap/dist/css/bootstrap.min.css",
  "/assets/cms/assets/bower_components/font-awesome/css/font-awesome.min.css",
  "/assets/cms/assets/bower_components/Ionicons/css/ionicons.min.css",
  "/assets/cms/assets/dist/css/AdminLTE.css",
  "/assets/cms/assets/dist/css/skins/_all-skins.min.css",
  "/assets/cms/assets/bower_components/morris.js/morris.css",
  "/assets/cms/assets/bower_components/bootstrap-datepicker/dist/css/bootstrap-datepicker.min.css",
  "/assets/cms/assets/bower_components/bootstrap-daterangepicker/daterangepicker.css",
  "/assets/cms_custom.css",
];

const CMS_JS = [
  "/assets/cms/assets/bower_components/jquery/dist/jquery.min.js",
  "/assets/cms/assets/bower_components/jquery-ui/jquery-ui.min.js",
  "/assets/cms/assets/bower_components/bootstrap/dist/js/bootstrap.min.js",
  "/assets/cms/assets/bower_components/raphael/raphael.min.js",
  "/assets/cms/assets/bower_components/morris.js/morris.min.js",
  "/assets/cms/assets/bower_components/jquery-sparkline/dist/jquery.sparkline.min.js",
  "/assets/cms/assets/plugins/jvectormap/jquery-jvectormap-1.2.2.min.js",
  "/assets/cms/assets/plugins/jvectormap/jquery-jvectormap-world-mill-en.js",
  "/assets/cms/assets/bower_components/jquery-knob/dist/jquery.knob.min.js",
  "/assets/cms/assets/bower_components/moment/min/moment.min.js",
  "/assets/cms/assets/bower_components/bootstrap-daterangepicker/daterangepicker.js",
  "/assets/cms/assets/bower_components/bootstrap-datepicker/dist/js/bootstrap-datepicker.min.js",
  "/assets/cms/assets/bower_components/jquery-slimscroll/jquery.slimscroll.min.js",
  "/assets/cms/assets/bower_components/fastclick/lib/fastclick.js",
  "/assets/cms/assets/dist/js/adminlte.min.js",
  "/assets/cms/assets/dist/js/pages/dashboard.js",
];

/**
 * Dynamically injects / removes AdminLTE CSS & JS so they only
 * affect the page while the CMS section is active.
 */
export default function CmsLayout({ children }) {
  useOnlineTracker();
  const [cmsStylesReady, setCmsStylesReady] = useState(false);

  useLayoutEffect(() => {
    document.body.classList.add("skin-blue", "sidebar-mini", "sidebar-collapse");
    return () => {
      document.body.classList.remove("skin-blue", "sidebar-mini", "sidebar-collapse");
    };
  }, []);

  useEffect(() => {
    let disposed = false;

    // --- inject CSS ---
    const cssLoadTasks = [];
    const cssElements = CMS_CSS.map((href) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.dataset.cms = "true";
      cssLoadTasks.push(
        new Promise((resolve) => {
          link.onload = resolve;
          link.onerror = resolve;
        })
      );
      document.head.appendChild(link);
      return link;
    });

    // Also load jvectormap CSS from CDN
    const jvmCss = document.createElement("link");
    jvmCss.rel = "stylesheet";
    jvmCss.href =
      "https://cdnjs.cloudflare.com/ajax/libs/jvectormap/2.0.4/jquery-jvectormap.css";
    jvmCss.dataset.cms = "true";
    cssLoadTasks.push(
      new Promise((resolve) => {
        jvmCss.onload = resolve;
        jvmCss.onerror = resolve;
      })
    );
    document.head.appendChild(jvmCss);
    cssElements.push(jvmCss);

    // Google Font used by admin
    const gFont = document.createElement("link");
    gFont.rel = "stylesheet";
    gFont.href =
      "https://fonts.googleapis.com/css?family=Source+Sans+Pro:300,400,600,700,300italic,400italic,600italic";
    gFont.dataset.cms = "true";
    cssLoadTasks.push(
      new Promise((resolve) => {
        gFont.onload = resolve;
        gFont.onerror = resolve;
      })
    );
    document.head.appendChild(gFont);
    cssElements.push(gFont);

    Promise.all(cssLoadTasks).then(() => {
      if (!disposed) setCmsStylesReady(true);
    });

    // --- inject JS (in order) ---
    const scriptElements = [];
    let chain = Promise.resolve();
    CMS_JS.forEach((src) => {
      chain = chain.then(
        () =>
          new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = src;
            script.dataset.cms = "true";
            script.onload = resolve;
            script.onerror = resolve; // continue even if one fails
            document.body.appendChild(script);
            scriptElements.push(script);
          })
      );
    });

    // Cleanup when leaving CMS
    return () => {
      disposed = true;
      setCmsStylesReady(false);
      cssElements.forEach((el) => el.remove());
      scriptElements.forEach((el) => el.remove());
    };
  }, []);

  return (
    <div className="wrapper">
      {cmsStylesReady ? (
        children
      ) : (
        <div className="cms-boot-loading" aria-busy="true" aria-live="polite" />
      )}
    </div>
  );
}
